import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from "react-router-dom";

import PageLayout from "../../components/PageLayout";
import { RouteData } from "../../types/types";
import BookingForm from "./BookingForm";
import MapSection from "./MapSection";
import { SectionHeading } from "../../components/SectionHeading";
import { SuccessAlert } from '../../components/SuccessAlert';
import { createOrder } from '../../services/orderService';
import { fetchInputTips } from "../../services/amapService";
import { getHongKongDateTime } from "../../utils/getHongKongDateTime";
import { getDistance } from '../../utils/mileage';
import { useToken } from '../../context/TokenContext';
import { useBooking } from "../../context/BookingContext";
import BookingAuth from "../../components/Auth/BookingAuth";
import {useTranslation} from "react-i18next";
import { validateLocationInput } from "../../utils/createOrderHelper";
import {GBACity} from "../../utils/Constants";

// Constants
const API_KEY = process.env.REACT_APP_AMAP_KEY || "";
const DEFAULT_MIDPOINT = "114.133300,22.500118";
const DEFAULT_RIDE_OPTION = "舒适";
const DEFAULT_PASSENGERS = 2;
const DEFAULT_LUGGAGE = 0;
const DEFAULT_MILEAGE = 0;

interface BookingSectionProps {
  onOrderSuccess: () => void;
  onInputError: (message: string) => void;
}

interface MapState {
  midPointLoc: string;
  routeData: RouteData | undefined;
  getRouteData: boolean;
}

interface FormState {
  pickupTime: string;
  pickupTimeError: string;
  passengers: number;
  luggage: number;
  rideOption: string;
  mileage: number;
}

const BookingSection: React.FC<BookingSectionProps> = React.memo(({ onOrderSuccess, onInputError }) => {
  const { t } = useTranslation();
  const [mapState, setMapState] = useState<MapState>({
    midPointLoc: DEFAULT_MIDPOINT,
    routeData: undefined,
    getRouteData: false
  });

  const [formState, setFormState] = useState<FormState>({
    pickupTime: getHongKongDateTime(),
    pickupTimeError: "",
    passengers: DEFAULT_PASSENGERS,
    luggage: DEFAULT_LUGGAGE,
    rideOption: DEFAULT_RIDE_OPTION,
    mileage: DEFAULT_MILEAGE,
  });

  const [openBookingAuth, setOpenBookingAuth] = useState(false);

  const { loginToken, serviceToken } = useToken();
  const { phoneNumber, countryPrefix } = useBooking();

  const errorMessage = !API_KEY ? "Error: Map API key is not configured" : "";

  const updateFormField = useCallback(<K extends keyof FormState>(field: K, value: FormState[K]) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleOrderCreate = useCallback(
    async (location: string, destination: string, locationCity: GBACity, destinationCity: GBACity): Promise<RouteData | undefined> => {
      try {
        // Fetch location and destination data in parallel
        const [dataLocation, dataDestination] = await Promise.all([
          fetchInputTips({
            keywords: `${locationCity}, ${location}`,
            apiKey: API_KEY
          }),
          fetchInputTips({
            keywords: `${destinationCity}, ${destination}`,
            apiKey: API_KEY
          }),
        ]);


        // Check if no results were found for either location or destination
        if (dataLocation.count === "0") {
          onInputError(t('BookingInvalidLocationInput'));
          return undefined;
        }
        if (dataDestination.count === "0") {
          onInputError(t('BookingInvalidDestinationInput'))
          return undefined;
        }

        // Construct the RouteData object
        const data: RouteData = {
          location: dataLocation.tips[0],
          destination: dataDestination.tips[0],
        };

        if (!validateLocationInput(data.location.district, data.destination.district)) {
          onInputError(t('BookingInvalidCreateOrder'));
          return undefined;
        }

        const distance = getDistance([data.location.district, data.destination.district]);
        updateFormField('mileage', distance);

        // Calculate midpoint for map state
        const [lng1, lat1] = data.location.location.split(",").map(parseFloat);
        const [lng2, lat2] = data.destination.location.split(",").map(parseFloat);

        // Update map state
        setMapState({
          midPointLoc: `${(lng1 + lng2) / 2},${(lat1 + lat2) / 2}`,
          routeData: data,
          getRouteData: true,
        });

        return data;
      } catch (error) {
        console.error("Error in handleOrderCreate:", error);
        return undefined;
      }
    }, []
  );

  const handleConfirmOrder = useCallback(async (): Promise<void> => {
    if (!loginToken || !serviceToken) {
      console.error("Missing loginToken or serviceToken");
      setOpenBookingAuth(true);
      return;
    }

    try {
      const response = await createOrder({
        pickupTime: formState.pickupTime,
        passengers: formState.passengers,
        luggage: formState.luggage,
        rideOption: formState.rideOption,
        phoneNumber,
        countryPrefix,
        routeData: mapState.routeData,
        loginToken,
        serviceToken,
      });
      // console.log("Order created successfully:", response);
    } catch (error) {
      console.error("Error creating order:", error);
    }
    onOrderSuccess();
    // Reset form and map state after successful order creation
    handleCancelOrder();
  }, [
    formState,
    mapState.routeData,
    phoneNumber,
    countryPrefix,
    loginToken,
    serviceToken,
    onOrderSuccess,
  ]);

  const handleCancelOrder = useCallback((): void => {
    setFormState({
      pickupTime: getHongKongDateTime(),
      pickupTimeError: "",
      passengers: DEFAULT_PASSENGERS,
      luggage: DEFAULT_LUGGAGE,
      rideOption: DEFAULT_RIDE_OPTION,
      mileage: DEFAULT_MILEAGE,
    });

    setMapState({
      midPointLoc: DEFAULT_MIDPOINT,
      routeData: undefined,
      getRouteData: false
    });
  }, []);

  if (errorMessage) {
    return <div className="text-red-500 text-center mt-10">{errorMessage}</div>;
  }

  return (
    <div className="flex flex-col lg:grid lg:grid-cols-5 gap-4 my-6 md:my-10">
      {/* Form Section - Full width on mobile, 2/5 on desktop */}
      <div className="lg:col-span-2 flex flex-col justify-start text-left space-y-4 md:space-y-6 px-4 md:px-8 order-2 lg:order-1">
        <div>
          {!mapState.getRouteData ? (
            <>
              <SectionHeading className="text-center lg:text-left">{t('BookingHero')}</SectionHeading>
              <p className="mt-2 text-sm md:text-base text-center lg:text-left">{t('BookingHeroNote')}</p>
            </>
          ) : (
            <SectionHeading size="sm" className="text-center lg:text-left">{t('BookingTripDetails')}</SectionHeading>
          )}
        </div>
        <BookingForm
          onOrderCreate={handleOrderCreate}
          onPickupTimeChange={(value) => updateFormField('pickupTime', value)}
          onPassengersChange={(value) => updateFormField('passengers', value)}
          onLuggageChange={(value) => updateFormField('luggage', value)}
          onPickupTimeErrorChange={(value) => updateFormField('pickupTimeError', value)}
          pickupTime={formState.pickupTime}
          pickupTimeError={formState.pickupTimeError}
          passengers={formState.passengers}
          luggage={formState.luggage}
          orderCreated={!!mapState.routeData}
          onConfirmOrder={handleConfirmOrder}
          onCancelOrder={handleCancelOrder}
          mileage={formState.mileage}
        />
      </div>

      {/* Map Section - Full width on mobile, 3/5 on desktop */}
      <div className="lg:col-span-3 order-1 lg:order-2 mb-4 lg:mb-0">
        <MapSection
          apiKey={API_KEY}
          midPointLoc={mapState.midPointLoc}
          routeData={mapState.routeData}
          pickupTime={formState.pickupTime}
          passengers={formState.passengers}
          rideOption={formState.rideOption}
          onRideOptionChange={(value) => updateFormField('rideOption', value)}
        />
      </div>

      {/* Show BookingAuth modal when openBookingAuth is true */}
      {openBookingAuth && (
        <BookingAuth onClose={() => setOpenBookingAuth(false)} />
      )}
    </div>
  );
});

BookingSection.displayName = 'BookingSection';

interface UiState {
  isLoading: boolean;
  redirectDestination: string;
  alertMessage: string | null;
  success?: boolean;
}

const BookingPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [uiState, setUiState] = useState<UiState>({
    isLoading: true,
    redirectDestination: '/',
    alertMessage: null,
    success: true,
  });

  const handleInputError = useCallback((message: string): void => {
    setUiState(prev => ({ ...prev, redirectDestination: '/book', alertMessage: message, success: false }));
  }, []);

  const handleOrderSuccess = useCallback((): void => {
    const message = t('BookingOrderSuccess');
    setUiState(prev => ({ ...prev, alertMessage: message }));
  }, [t]);

  const closeAlert = useCallback((): void => {
    setUiState(prev => ({ ...prev, alertMessage: null }));
  }, []);

  const handleRedirect = useCallback((destination: string): void => {
    navigate(destination);
  }, [navigate]);

  const preloadMap = useCallback(() => {
    const mapImage = new Image();
    mapImage.src = `https://restapi.amap.com/v3/staticmap?location=${DEFAULT_MIDPOINT}&zoom=9&size=950*580&key=${API_KEY}`;
  }, []);

  useEffect(() => {
    if (!uiState.isLoading) {
      preloadMap();
    }
  }, [uiState.isLoading, preloadMap]);

  return (
    <PageLayout isBooking={true}>
      <div className="flex justify-center items-center w-full pt-20 md:pt-24 lg:pt-28 px-4 sm:px-6">
        <main className="flex flex-col items-center w-full sm:w-[90%] md:w-[85%] lg:w-[80%]">
          <BookingSection onOrderSuccess={handleOrderSuccess} onInputError={handleInputError}/>
        </main>
      </div>
      {uiState.alertMessage && (
        <SuccessAlert
          message={uiState.alertMessage}
          onClose={closeAlert}
          onRedirect={handleRedirect}
          redirectDestination={uiState.redirectDestination}
          success={uiState.success}
        />
      )}
    </PageLayout>
  );
};

export default BookingPage;